import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/** Opção mínima; `User` do app é compatível (id, name, email?, role?). */
export interface SearchableSelectOption {
  id: string;
  name: string;
  email?: string;
  role?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  /** Nome do campo no `formData` (ex.: managerId, developerId, cycleId). */
  fieldName?: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
  placeholder?: string;
  label: string;
  required?: boolean;
  /** Mantido por compatibilidade; Autocomplete sempre filtra. */
  showSearch?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  required,
  fieldName = 'managerId',
}) => {
  const selectedOption = options.find((opt) => opt.id === value) ?? null;

  return (
    <Autocomplete<SearchableSelectOption>
      options={options}
      value={selectedOption}
      onChange={(_event, option) =>
        onChange({ target: { name: fieldName, value: option?.id ?? '' } })
      }
      getOptionLabel={(option) =>
        option.email ? `${option.name} (${option.email})` : option.name
      }
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {option.name}
            </Typography>
            {option.email && (
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            )}
            {option.role && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {option.role}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      noOptionsText="Nenhum resultado encontrado"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          fullWidth
        />
      )}
    />
  );
};

export default SearchableSelect;
